import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi, type AdwordsDay, type AdwordsHour } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { localDate } from '../../lib/format.js';

/**
 * 애드워즈 현황 — 원본 /home/bananapartner/xadmin/report_adw.php 를 **표 그대로** 옮긴 화면.
 *
 * 같은 자료를 다르게 보는 화면이 둘이다:
 *   /xadm/adwords              이 화면. 원본의 16열 표 + 합계, 원본의 상세(mode=detail) 표.
 *   /xadm/adwords-performance  같은 자료를 요약·추세 중심으로 다시 그린 '애드워즈 성과'.
 * 자료와 API(`/api/xadm/adwords`)는 하나를 같이 쓴다 - 계산은 서버
 * (modules/xadm/adwords.service.ts)에 한 벌만 있다.
 *
 * 원본과 같게 둔 것
 *  - 열 순서·이름·계산식 전부 (객단가는 1,000 단위 반올림, 클릭단가는 소수 둘째 자리).
 *  - 기본 기간 30일 전~오늘, 일자 내림차순, 요일 버튼(일~토), 초기화 버튼.
 *  - 전환건 200/300/400 구간 강조(원본 :199 의 배경색 분기).
 *  - 일자를 누르면 그 날의 시간대별 표가 오른쪽에 붙는다(원본 mode=detail).
 *
 * 원본과 다르게 한 것
 *  - 검은 배경·인라인 색상 대신 이 관리자 화면의 테마를 쓴다(글자·숫자는 그대로다).
 *  - 분모가 0 인 비율은 '-' 다. 원본은 PHP 의 @ 로 경고만 지워서 INF/NAN 이 찍혔다.
 *  - 원본 머리글의 정렬 링크 중 orderby=no_join·no_joinm 은 **그런 컬럼이 없어
 *    SQL 이 깨지던 죽은 링크**였다. 살아 있던 일자 정렬만 남겼다.
 *  - 상세 표에서 같은 시각이 7번씩 반복되던 것은 시각으로 접었다
 *    (adwords.service.ts 의 loadAdwordsHours 주석 참고).
 *  - (P)전환·(P)전환금액 입력칸과 저장·자동조회 버튼(report_adw_ajax.php)은
 *    원본에서도 이미 주석 처리돼 화면에 없었다. 그래서 옮기지 않았다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** 원본 :199 - 전환건이 많은 날은 눈에 띄게 (200/300/400 구간). */
function convClass(conv: number): string | undefined {
  if (conv >= 400) return 'adw-conv adw-conv--top';
  if (conv >= 300) return 'adw-conv adw-conv--high';
  if (conv >= 200) return 'adw-conv adw-conv--mid';
  return undefined;
}

/** 원본 number_format() 자리. 값이 없으면(분모 0) 빈 칸이 아니라 '-' 로 둔다. */
const n = (value: number) => value.toLocaleString('en-US');
const rate = (value: number | null) => (value === null ? '-' : `${value}%`);
const won = (value: number | null) => (value === null ? '-' : value.toLocaleString('en-US'));

function daysAgo(days: number) {
  return localDate(new Date(Date.now() - days * 86_400_000));
}

export function AdwordsStatusPage() {
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? daysAgo(30);
  const to = params.get('to') ?? localDate();
  const weekdayParam = params.get('weekday');
  const weekday = weekdayParam !== null && /^[0-6]$/.test(weekdayParam) ? Number(weekdayParam) : null;
  const [draft, setDraft] = useState({ from, to });
  /** 원본 mode=detail&dates=... - 고른 일자의 시간대별 표. */
  const [detailDate, setDetailDate] = useState<string | null>(null);

  const daily = useQuery({
    queryKey: ['xadm-adwords-status', from, to, weekday],
    queryFn: () => xadmApi.adwords({ from, to, weekday, sort: 'dates' }),
  });

  const hours = useQuery({
    queryKey: ['xadm-adwords-hours', detailDate],
    queryFn: () => xadmApi.adwordsHours(detailDate!),
    enabled: detailDate !== null,
  });

  function apply(next: Partial<{ from: string; to: string; weekday: number | null }> = {}) {
    const query: Record<string, string> = {
      from: next.from ?? draft.from,
      to: next.to ?? draft.to,
    };
    const nextWeekday = next.weekday === undefined ? weekday : next.weekday;
    if (nextWeekday !== null) query.weekday = String(nextWeekday);
    setParams(query, { replace: true });
    setDetailDate(null);
  }

  /** 원본 '초기화' - 파라미터 없이 다시 연다(30일, 전체 요일). */
  function reset() {
    setDraft({ from: daysAgo(30), to: localDate() });
    setParams({}, { replace: true });
    setDetailDate(null);
  }

  return (
    <section className="adws-page">
      <header className="page-head">
        <div>
          <h1 className="page-title">애드워즈 현황</h1>
          <p className="page-desc">
            원본 report_adw.php 의 표를 그대로 옮긴 화면입니다. 일자를 누르면 그 날의 시간대별 내역이 오른쪽에
            붙습니다. 같은 자료를 요약·추세로 보려면 '애드워즈 성과' 화면을 쓰세요.
          </p>
        </div>
        {daily.isFetching && <span className="page-head__busy">갱신 중…</span>}
      </header>

      <form
        className="toolbar adws-filter"
        onSubmit={(event) => {
          event.preventDefault();
          apply();
        }}
      >
        <label className="adws-filter__field">
          일자검색
          <input
            type="date"
            className="input"
            aria-label="시작일"
            value={draft.from}
            onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
          />
        </label>
        <span aria-hidden="true">~</span>
        <input
          type="date"
          className="input"
          aria-label="종료일"
          value={draft.to}
          onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
        />
        <button type="submit" className="btn btn--primary btn--sm">검색</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={reset}>초기화</button>
        <div className="tab-group" aria-label="요일">
          {WEEKDAYS.map((label, day) => (
            <button
              key={label}
              type="button"
              className={weekday === day ? 'tab-btn tab-btn--active' : 'tab-btn'}
              aria-pressed={weekday === day}
              onClick={() => apply({ weekday: weekday === day ? null : day })}
            >
              {label}
            </button>
          ))}
        </div>
      </form>

      <div className="adws-layout">
        <div className="table-wrap adws-table-wrap">
          {daily.isPending ? (
            <Spinner />
          ) : daily.isError ? (
            <ErrorBox error={daily.error} onRetry={() => void daily.refetch()} />
          ) : daily.data.days.length === 0 ? (
            <EmptyBox>이 구간에는 자료가 없습니다.</EmptyBox>
          ) : (
            <table className="table table--dense adws-table">
              <thead>
                <tr>
                  <th>일자</th>
                  <th>요일</th>
                  <th>시간</th>
                  <th>전환수</th>
                  <th>클릭수</th>
                  <th>노출수</th>
                  <th>클릭율</th>
                  <th>광고금액</th>
                  <th>전환금액</th>
                  <th>수익율</th>
                  <th>객단가</th>
                  <th>클릭단가</th>
                  <th className="adws-p">(P)전환수</th>
                  <th>(P)전환금액</th>
                  <th>(P)수익율</th>
                  <th>(P)객단가</th>
                </tr>
              </thead>
              <tbody>
                {daily.data.days.map((day) => (
                  <DayRow
                    key={day.date}
                    day={day}
                    isOpen={detailDate === day.date}
                    onOpen={() => setDetailDate((current) => (current === day.date ? null : day.date))}
                  />
                ))}
              </tbody>
              <tfoot>
                <TotalRow totals={daily.data.totals} />
              </tfoot>
            </table>
          )}
        </div>

        {detailDate !== null && (
          <section className="panel adws-detail" aria-label={`${detailDate} 시간대별 현황`}>
            <header className="adws-detail__head">
              <h2>{detailDate} 현황</h2>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDetailDate(null)}>
                닫기
              </button>
            </header>
            {hours.isPending ? (
              <Spinner />
            ) : hours.isError ? (
              <ErrorBox error={hours.error} onRetry={() => void hours.refetch()} />
            ) : (hours.data?.hours.length ?? 0) === 0 ? (
              <EmptyBox>{detailDate} 자료가 없습니다.</EmptyBox>
            ) : (
              <HourTable rows={hours.data!.hours} />
            )}
          </section>
        )}
      </div>
    </section>
  );
}

function DayRow({ day, isOpen, onOpen }: { day: AdwordsDay; isOpen: boolean; onOpen: () => void }) {
  return (
    <tr className={isOpen ? 'table__row--clickable adws-row--open' : 'table__row--clickable'} onClick={onOpen}>
      <td>
        {/* 원본은 모든 칸이 mode=detail 링크였다. 여기서는 행 전체가 그 링크다. */}
        {/* 행에도 같은 onClick 이 있다. 막지 않으면 버블링으로 두 번 토글돼 그대로 닫힌다. */}
        <button
          type="button"
          className="link-btn"
          aria-expanded={isOpen}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          {day.date}
        </button>
      </td>
      <td className={day.weekday === 0 ? 'td-danger' : day.weekday === 6 ? 'adw-sat' : undefined}>
        {WEEKDAYS[day.weekday] ?? '-'}
      </td>
      <td>{day.lastHour}</td>
      <td className="td-right"><span className={convClass(day.conv)}>{n(day.conv)}</span></td>
      <td className="td-right">{n(day.click)}</td>
      <td className="td-right">{n(day.imp)}</td>
      <td className="td-right">{rate(day.clickRate)}</td>
      <td className="td-right">{n(day.cost)}</td>
      <td className="td-right">{n(day.convcost)}</td>
      <td className="td-right">{rate(day.profitRate)}</td>
      <td className="td-right">{won(day.avgOrder)}</td>
      <td className="td-right">{won(day.clickCost)}</td>
      <td className="td-right adws-p">{n(day.porder)}</td>
      <td className="td-right">{n(day.pconvcost)}</td>
      <td className="td-right">{rate(day.pProfitRate)}</td>
      <td className="td-right">{won(day.pAvgOrder)}</td>
    </tr>
  );
}

/** 원본의 마지막 '합계' 행. (P)수익율·(P)객단가는 원본도 빈 칸이었다. */
function TotalRow({ totals }: { totals: Omit<AdwordsDay, 'date' | 'weekday' | 'lastHour'> }) {
  return (
    <tr className="row--total">
      <td>합계</td>
      <td />
      <td />
      <td className="td-right">{n(totals.conv)}</td>
      <td className="td-right">{n(totals.click)}</td>
      <td className="td-right">{n(totals.imp)}</td>
      <td className="td-right">{rate(totals.clickRate)}</td>
      <td className="td-right">{n(totals.cost)}</td>
      <td className="td-right">{n(totals.convcost)}</td>
      <td className="td-right">{rate(totals.profitRate)}</td>
      <td className="td-right">{won(totals.avgOrder)}</td>
      <td className="td-right">{won(totals.clickCost)}</td>
      <td className="td-right adws-p">{n(totals.porder)}</td>
      <td className="td-right">{n(totals.pconvcost)}</td>
      <td />
      <td />
    </tr>
  );
}

/**
 * 원본 mode=detail 표. 첫 '전환' 열이 그 시간 동안의 증분($conv_per_hour),
 * 둘째 '전환' 열이 그 시각까지의 누계다. 원본에 주석으로 막혀 있던 열
 * (전일·전주 대비 증감)은 옮기지 않았다.
 */
function HourTable({ rows }: { rows: AdwordsHour[] }) {
  return (
    <div className="table-wrap">
      <table className="table table--dense table--nested adws-hour-table">
        <thead>
          <tr>
            <th>시간</th>
            <th>전환(시간당)</th>
            <th>전환</th>
            <th>클릭</th>
            <th>노출</th>
            <th>클릭율</th>
            <th>광고금액</th>
            <th>전환금액</th>
            <th>수익율</th>
            <th>객단가</th>
            <th className="adws-p">(P)전환</th>
            <th>(P)전환금액</th>
            <th>(P)수익율</th>
            <th>(P)객단가</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((hour) => (
            <tr key={hour.hour}>
              <td>{hour.hour}</td>
              <td className="td-right">{n(hour.convPerHour)}</td>
              <td className="td-right">{n(hour.conv)}</td>
              <td className="td-right">{n(hour.click)}</td>
              <td className="td-right">{n(hour.imp)}</td>
              <td className="td-right">{rate(hour.clickRate)}</td>
              <td className="td-right">{n(hour.cost)}</td>
              <td className="td-right">{n(hour.convcost)}</td>
              <td className="td-right">{rate(hour.profitRate)}</td>
              <td className="td-right">{won(hour.avgOrder)}</td>
              <td className="td-right adws-p">{n(hour.porder)}</td>
              <td className="td-right">{n(hour.pconvcost)}</td>
              <td className="td-right">{rate(hour.pProfitRate)}</td>
              <td className="td-right">{won(hour.pAvgOrder)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
